import { partsSellPrice } from "@/lib/partsPrice";
import { supabase } from "@/integrations/supabase/client";

// Facet responses only change when inventory syncs run — cache them in-memory
// for 30 minutes so navigating between category pages never re-hits the DB.
const FACET_TTL_MS = 30 * 60 * 1000;
const facetCache = new Map<string, { at: number; value: unknown }>();
function facetCached<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const hit = facetCache.get(key);
  if (hit && Date.now() - hit.at < FACET_TTL_MS) return Promise.resolve(hit.value as T);
  return loader().then((v) => {
    facetCache.set(key, { at: Date.now(), value: v });
    return v;
  });
}

export interface CdnProduct {
  ProductID: string | number;
  product_id?: string | number;
  Slug?: string;
  admin_slug?: string | null;
  short_slug?: string | null;
  ProductTitle?: string;
  product_title?: string;
  Title?: string;
  Name?: string;
  CustomerRetailPrice?: number | string;
  CustomerCost?: number | string;
  customer_cost?: number | string | null;
  customer_retail_price?: number | string | null;
  admin_price?: number | string | null;
  AdminPrice?: number | string | null;
  JobberPrice?: number | string;
  MAPretail?: number | string | null;
  map_retail?: number | string | null;
  Price?: string | number;
  SalePrice?: string | number;
  admin_sale_price?: string | number | null;
  Description?: string;
  MarketingDescription?: string;
  ApplicationSummaryText?: string;
  ApplicationModels?: string[];
  MetaTitle?: string;
  MetaDescription?: string;
  MetaKeywords?: string;
  PartTypeName?: string;
  VendorName?: string;
  Image_1_Url?: string;
  Image_2_Url?: string;
  Image_3_Url?: string;
  Image_4_Url?: string;
  Image_5_Url?: string;
  Image_6_Url?: string;
  InventoryTotal?: number;
  Inv_East?: number;
  Inv_GreatLakes?: number;
  Inv_Midwest?: number;
  Inv_PacificNorthwest?: number;
  Inv_Texas?: number;
  Inv_California?: number;
  Inv_Southeast?: number;
  Inv_Florida?: number;
  Fedexable?: number | string;
  UPSable?: string;
  US_LTL?: string;
  UPS_Ground_Assessorial?: string;
  DescriptionHtml?: string;
  AdminImages?: string[];
  IsPublished?: boolean;
  addon_product_id?: string | null;
  related_addon_ids?: string[] | null;
  all_attributes_html?: string | null;
  all_features_html?: string | null;
  generic_legacy_name?: string | null;
  InstallPdfUrl?: string | null;
  DocLinks?: { label?: string | null; url?: string | null }[] | null;
  [k: string]: unknown;
}

export function productTitle(p: CdnProduct): string {
  return (p.ProductTitle || p.product_title || p.Title || p.MetaTitle || p.Name || `Product ${p.ProductID ?? p.product_id}`) as string;
}

/**
 * SEO-friendly heading for a product. Uses the (typically shorter) MetaTitle
 * when available so search engines and shoppers see a concise primary heading,
 * falling back to the long product title.
 */
export function productHeading(p: CdnProduct): string {
  const meta = (p.MetaTitle as string | undefined)?.trim();
  if (meta) return stripVendorPrefix(meta, p);
  return productTitle(p);
}

/**
 * Strips an upstream vendor-code token from the front of a meta title when
 * that leading token ends with the product's real part number — mirrors the
 * SQL `cdn_strip_vendor_prefix` used to build the short slug, so the H1 stays
 * consistent with the URL.
 */
function stripVendorPrefix(meta: string, p: CdnProduct): string {
  const pid = String(p.ProductID ?? p.product_id ?? "").trim();
  if (!pid) return meta;
  const firstToken = meta.split(/\s+/, 1)[0] ?? "";
  if (firstToken && firstToken.toLowerCase().endsWith(pid.toLowerCase())) {
    // drop first token plus any whitespace / dash / en-dash / em-dash / colon separator
    return meta.replace(/^\S+[\s\-–—:]+/, "").trim();
  }
  return meta;
}

/**
 * The longer descriptive title (typically the vendor's full product title).
 * Returned only when it actually differs from the heading so callers can
 * render it as a subtitle without duplicating the H1.
 */
export function productSubheading(p: CdnProduct): string | null {
  const heading = productHeading(p);
  const long = productTitle(p);
  if (!long || long === heading) return null;
  return long;
}

export function productPrice(p: CdnProduct): number | null {
  const toNum = (v: unknown): number | null => {
    if (v == null || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  // Order must match the cart / checkout / PayPal pricing order:
  // staff sale price, staff/bulk override, MAP, retail, then any feed price.
  return (
    partsSellPrice({
      sale: toNum(p.SalePrice) ?? toNum(p.admin_sale_price),
      override: toNum(p.AdminPrice) ?? toNum(p.admin_price),
      map: toNum(p.MAPretail) ?? toNum(p.map_retail),
      cost: toNum((p as any).CustomerCost) ?? toNum((p as any).customer_cost),
      retail: toNum(p.CustomerRetailPrice) ?? toNum(p.customer_retail_price),
    }) ?? toNum(p.Price)
  );
}

export interface ProductListResponse {
  products?: CdnProduct[];
  data?: CdnProduct[];
  items?: CdnProduct[];
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number | undefined;
  [k: string]: unknown;
}

/** Normalize the API response into a stable list shape. */
export function extractProducts(res: ProductListResponse | CdnProduct[]): CdnProduct[] {
  if (Array.isArray(res)) return res;
  return res.products ?? res.data ?? res.items ?? [];
}

// Map a snake_case row from `cdn_products` to the PascalCase shape used by the UI.
export function rowToProduct(r: any): CdnProduct {
  if (!r) return r;
  const adminImgs: string[] = Array.isArray(r.admin_images) ? r.admin_images.filter(Boolean) : [];
  const useAdminImgs = adminImgs.length > 0;
  const feedTitle = r.product_title ?? undefined;
  const adminTitle = r.admin_title ?? undefined;
  const feedPrice = r.customer_retail_price ?? undefined;
  const adminPrice = r.admin_price ?? undefined;
  const pickPrice = (a: any, b: any) => {
    const an = a == null ? NaN : Number(a);
    return Number.isFinite(an) && an > 0 ? a : b;
  };
  return {
    ProductID: r.product_id,
    Slug: r.admin_slug ?? undefined,
    admin_slug: r.admin_slug ?? null,
    short_slug: r.short_slug ?? null,
    ProductTitle: adminTitle || feedTitle,
    MetaTitle: r.admin_meta_title ?? r.meta_title ?? undefined,
    MetaDescription: r.admin_meta_description ?? r.meta_description ?? undefined,
    MetaKeywords: r.admin_meta_keywords ?? undefined,
    Description: r.description ?? undefined,
    MarketingDescription: r.marketing_description ?? undefined,
    DescriptionHtml: r.description_html ?? undefined,
    PartTypeName: r.part_type_name ?? undefined,
    VendorName: r.vendor_name ?? undefined,
    CustomerRetailPrice: pickPrice(adminPrice, feedPrice),
    AdminPrice: adminPrice ?? null,
    JobberPrice: r.jobber_price ?? undefined,
    MAPretail: r.map_retail ?? undefined,
    CustomerCost: r.customer_cost ?? undefined,
    SalePrice: r.admin_sale_price ?? undefined,
    ApplicationModels: Array.isArray(r.application_models) ? r.application_models : undefined,
    Image_1_Url: useAdminImgs ? adminImgs[0] : r.image_1_url ?? undefined,
    Image_2_Url: useAdminImgs ? adminImgs[1] : r.image_2_url ?? undefined,
    Image_3_Url: useAdminImgs ? adminImgs[2] : r.image_3_url ?? undefined,
    Image_4_Url: useAdminImgs ? adminImgs[3] : r.image_4_url ?? undefined,
    Image_5_Url: useAdminImgs ? adminImgs[4] : r.image_5_url ?? undefined,
    Image_6_Url: useAdminImgs ? adminImgs[5] : r.image_6_url ?? undefined,
    AdminImages: useAdminImgs ? adminImgs : undefined,
    IsPublished: r.is_published ?? true,
    InventoryTotal:
      r.admin_stock_quantity != null
        ? Number(r.admin_stock_quantity)
        : r.inventory_total ?? undefined,
    Inv_East: r.inv_east ?? undefined,
    Inv_GreatLakes: r.inv_great_lakes ?? undefined,
    Inv_Midwest: r.inv_midwest ?? undefined,
    Inv_PacificNorthwest: r.inv_pacific_northwest ?? undefined,
    Inv_Texas: r.inv_texas ?? undefined,
    Inv_California: r.inv_california ?? undefined,
    Inv_Southeast: r.inv_southeast ?? undefined,
    Inv_Florida: r.inv_florida ?? undefined,
    Fedexable: r.fedexable ? 1 : 0,
    UPSable: r.upsable ? "True" : "False",
    US_LTL: r.us_ltl ?? undefined,
    UPS_Ground_Assessorial: r.ups_ground_assessorial ?? undefined,
    // raw shipping fields (used by shipping calculator)
    fedexable: !!r.fedexable,
    upsable: !!r.upsable,
    us_ltl: r.us_ltl ?? null,
    ups_ground_assessorial: r.ups_ground_assessorial ?? null,
    free_shipping: !!r.free_shipping,
    additional_shipping_charge: r.additional_shipping_charge ?? null,
    // keep original fields for reference
    product_number: r.product_number,
    vendor_code_product_number: r.vendor_code_product_number,
    upc: r.upc,
    manufacturer_part_no: r.manufacturer_part_no ?? null,
    admin_mpn: r.admin_mpn ?? null,
    admin_gtin: r.admin_gtin ?? null,
    basic_category: r.basic_category,
    addon_product_id: r.addon_product_id ?? null,
    related_addon_ids: Array.isArray(r.related_addon_ids) ? r.related_addon_ids : null,
    all_attributes_html: r.all_attributes_html ?? null,
    all_features_html: r.all_features_html ?? null,
    generic_legacy_name: r.generic_legacy_name ?? null,
    InstallPdfUrl: r.install_pdf_url ?? null,
    DocLinks: Array.isArray(r.doc_links) ? r.doc_links : null,
  } as CdnProduct;
}

function applySort(query: any, sort?: string) {
  switch (sort) {
    case "name_desc":
      return query.order("product_title", { ascending: false, nullsFirst: false });
    case "price_asc":
      return query.order("customer_retail_price", { ascending: true, nullsFirst: false });
    case "price_desc":
      return query.order("customer_retail_price", { ascending: false, nullsFirst: false });
    case "alpha_asc":
    case "name_asc":
    default:
      return query.order("product_title", { ascending: true, nullsFirst: false });
  }
}

// Map a row returned by the `cdn_products_search` RPC to the UI shape.
// The RPC returns the same column set listProducts selects, so this mirrors
// rowToProduct but is kept separate to make it easy to evolve the RPC
// projection independently.
function rpcRowToProduct(r: any): CdnProduct {
  if (!r) return r;
  const adminImgs: string[] = Array.isArray(r.admin_images) ? r.admin_images.filter(Boolean) : [];
  const useAdmin = adminImgs.length > 0;
  const pickPrice = (a: any, b: any) => {
    const an = a == null ? NaN : Number(a);
    return Number.isFinite(an) && an > 0 ? a : b;
  };
  return {
    ProductID: r.product_id,
    Slug: r.admin_slug ?? undefined,
    admin_slug: r.admin_slug ?? null,
    short_slug: r.short_slug ?? null,
    ProductTitle: r.admin_title || r.product_title,
    MetaTitle: r.meta_title ?? undefined,
    MetaDescription: r.meta_description ?? undefined,
    Description: r.description ?? undefined,
    MarketingDescription: r.marketing_description ?? undefined,
    PartTypeName: r.part_type_name ?? undefined,
    VendorName: r.vendor_name ?? undefined,
    CustomerRetailPrice: pickPrice(r.admin_price, r.customer_retail_price),
    AdminPrice: r.admin_price ?? null,
    MAPretail: r.map_retail ?? undefined,
    CustomerCost: r.customer_cost ?? undefined,
    SalePrice: r.admin_sale_price ?? undefined,
    Image_1_Url: useAdmin ? adminImgs[0] : r.image_1_url ?? undefined,
    Image_2_Url: useAdmin ? adminImgs[1] : r.image_2_url ?? undefined,
    Image_3_Url: useAdmin ? adminImgs[2] : r.image_3_url ?? undefined,
    Image_4_Url: useAdmin ? adminImgs[3] : r.image_4_url ?? undefined,
    Image_5_Url: useAdmin ? adminImgs[4] : r.image_5_url ?? undefined,
    Image_6_Url: useAdmin ? adminImgs[5] : r.image_6_url ?? undefined,
    AdminImages: useAdmin ? adminImgs : undefined,
    IsPublished: r.is_published ?? true,
    InventoryTotal:
      r.admin_stock_quantity != null
        ? Number(r.admin_stock_quantity)
        : r.inventory_total ?? undefined,
    Inv_East: r.inv_east ?? undefined,
    Inv_GreatLakes: r.inv_great_lakes ?? undefined,
    Inv_Midwest: r.inv_midwest ?? undefined,
    Inv_PacificNorthwest: r.inv_pacific_northwest ?? undefined,
    Inv_Texas: r.inv_texas ?? undefined,
    Inv_California: r.inv_california ?? undefined,
    Inv_Southeast: r.inv_southeast ?? undefined,
    Inv_Florida: r.inv_florida ?? undefined,
    Fedexable: r.fedexable ? 1 : 0,
    UPSable: r.upsable ? "True" : "False",
    US_LTL: r.us_ltl ?? undefined,
    UPS_Ground_Assessorial: r.ups_ground_assessorial ?? undefined,
    fedexable: !!r.fedexable,
    upsable: !!r.upsable,
    us_ltl: r.us_ltl ?? null,
    ups_ground_assessorial: r.ups_ground_assessorial ?? null,
    free_shipping: !!r.free_shipping,
    additional_shipping_charge: r.additional_shipping_charge ?? null,
    product_number: r.product_number,
    vendor_code_product_number: r.vendor_code_product_number,
    upc: r.upc,
    basic_category: r.basic_category,
    relevance: r.relevance,
  } as CdnProduct;
}

export async function listProducts(opts: {
  page?: number;
  limit?: number;
  search?: string | undefined;
  category?: string | undefined;
  vendor?: string | undefined;
  vendorId?: string | undefined;
  sort?: string;
  newOnly?: boolean | undefined;
  partType?: string;
  series?: string[] | undefined;
  inStock?: boolean | undefined;
  priceMin?: number | undefined;
  priceMax?: number | undefined;
} = {}): Promise<ProductListResponse> {
  const page = Math.max(1, opts.page ?? 1);
  const limit = Math.max(1, Math.min(100, opts.limit ?? 20));
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  // Search path: delegate tokenization, filtering, ranking, and pagination
  // to the `cdn_products_search` RPC. This replaces the 1000-row fetch +
  // JS rerank with a single indexed query that returns only the page slice.
  const hasSearch = !!(opts.search && opts.search.trim().length > 0);
  const seriesArr = opts.series && opts.series.length > 0 ? opts.series : null;
  if (hasSearch && !opts.partType) {
    const isDefaultSort = !opts.sort || opts.sort === "name_asc";
    const rpcSort = isDefaultSort ? "relevance" : opts.sort!;
    const { data, error } = await supabase.rpc("cdn_products_search", {
      _q: opts.search!,
      _category: opts.category ?? null,
      _vendor: opts.vendor ?? null,
      _vendor_id: opts.vendorId ?? null,
      _new_only: !!opts.newOnly,
      _sort: rpcSort,
      _page: page,
      _limit: limit,
      _series: seriesArr,
      _in_stock: !!opts.inStock,
      _price_min: opts.priceMin ?? null,
      _price_max: opts.priceMax ?? null,
    } as any);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as any[];
    const total = rows.length > 0 ? Number((rows[0] as any).total_count ?? 0) : 0;
    const products = rows.map(rpcRowToProduct);
    return {
      products,
      page,
      limit,
      total,
      totalPages: Math.max(1, Math.ceil(total / limit)),
    };
  }

  let q = supabase
    .from("cdn_products")
    .select(
      "product_id, admin_slug, short_slug, product_title, admin_title, meta_title, meta_description, " +
        "description, marketing_description, " +
        "part_type_name, vendor_name, basic_category, " +
        "customer_retail_price, admin_price, map_retail, admin_sale_price, customer_cost, " +
        "image_1_url, image_2_url, image_3_url, image_4_url, image_5_url, image_6_url, admin_images, " +
        "inventory_total, admin_stock_quantity, inv_east, inv_great_lakes, inv_midwest, inv_pacific_northwest, " +
        "inv_texas, inv_california, inv_southeast, inv_florida, " +
        "fedexable, upsable, us_ltl, ups_ground_assessorial, free_shipping, additional_shipping_charge, " +
        "is_published, is_new_badge, product_number, vendor_code_product_number, upc",
      { count: "estimated" }
    )
    .eq("is_active", true)
    .eq("is_published", true);

  if (opts.category) q = q.eq("basic_category", opts.category);
  if (opts.vendor) q = q.eq("vendor_name", opts.vendor);
  if (opts.vendorId) q = q.ilike("vendor_code_product_number", `${opts.vendorId}%`);
  if (opts.newOnly) q = q.eq("is_new_badge", true);
  if (opts.partType) q = q.eq("part_type_name", opts.partType);
  if (seriesArr) q = q.in("generic_legacy_name", seriesArr);
  if (opts.inStock) q = q.gt("inventory_total", 0);
  if (opts.priceMin != null) q = q.gte("customer_retail_price", opts.priceMin);
  if (opts.priceMax != null) q = q.lte("customer_retail_price", opts.priceMax);
  if (hasSearch && opts.partType) {
    const tok = opts.search!.trim().replace(/[%_]/g, "");
    q = q.or(
      `product_title.ilike.%${tok}%,product_number.ilike.%${tok}%,vendor_code_product_number.ilike.%${tok}%,upc.ilike.%${tok}%`,
    );
  }

  q = applySort(q, opts.sort);
  q = q.range(from, to);

  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  const products = (data ?? []).map(rowToProduct);
  return {
    products,
    page,
    limit,
    total: count ?? products.length,
    totalPages: count != null ? Math.max(1, Math.ceil(count / limit)) : undefined,
  };
}

/**
 * Fast autocomplete: skips count, selects only the columns the suggestion
 * dropdown actually renders, and narrows the OR-search to the most useful
 * indexed text fields. ~10x faster than listProducts() for large catalogs.
 */
export async function listSuggestions(term: string, limit = 6): Promise<CdnProduct[]> {
  if (!term || !term.trim()) return [];
  const { data, error } = await supabase.rpc("cdn_products_search", {
    _q: term,
    _new_only: false,
    _sort: "relevance",
    _page: 1,
    _limit: limit,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[]).map(rpcRowToProduct);
}

export interface CdnCategory { name: string; count: number }

export interface CatalogFacetCounts {
  total: number;
  categories: CdnCategory[];
  vendors: CdnCategory[];
  series: CdnCategory[];
}

/**
 * Count-aware facets for the storefront filter rail. Counts for each facet are
 * computed with the *other* active filters applied.
 */
export async function getCatalogFacetCounts(opts: {
  search?: string | undefined;
  category?: string | undefined;
  vendor?: string | undefined;
  vendorId?: string | undefined;
  series?: string[] | undefined;
  inStock?: boolean | undefined;
  priceMin?: number | undefined;
  priceMax?: number | undefined;
} = {}): Promise<CatalogFacetCounts> {
  const { data, error } = await supabase.rpc("cdn_products_facet_counts", {
    _q: opts.search ?? null,
    _category: opts.category ?? null,
    _vendor: opts.vendor ?? null,
    _vendor_id: opts.vendorId ?? null,
    _series: opts.series && opts.series.length > 0 ? opts.series : null,
    _in_stock: !!opts.inStock,
    _price_min: opts.priceMin ?? null,
    _price_max: opts.priceMax ?? null,
  } as any);
  if (error) throw new Error(error.message);
  let d: any = data ?? {};
  if (typeof d === "string") {
    try { d = JSON.parse(d); } catch { d = {}; }
  }
  const asList = (v: any): CdnCategory[] =>
    Array.isArray(v)
      ? v
          .map((x: any) => ({ name: String(x.name), count: Number(x.count ?? 0) }))
          .filter((x) => !!x.name)
      : [];
  return {
    total: Number(d.total ?? 0),
    categories: asList(d.categories),
    vendors: asList(d.vendors),
    series: asList(d.series),
  };
}

/**
 * Distinct catalog facets (categories / vendors / part types) for active products.
 * Powered by the `cdn_products_facets()` RPC so the dropdowns don't pull
 * 50k rows on every page render. Counts are not returned by the RPC; callers
 * that only need the names should rely on this. For count-aware UIs use the
 * filtered variants below.
 */
let _facetsCache: Promise<{ categories: string[]; vendors: string[]; part_types: string[] }> | null = null;
export function getCdnFacets() {
  if (!_facetsCache) {
    _facetsCache = (async () => {
      const { data, error } = await supabase.rpc("cdn_products_facets");
      if (error) throw new Error(error.message);
      let d: any = data ?? {};
      if (typeof d === "string") {
        try { d = JSON.parse(d); } catch { d = {}; }
      }
      return {
        categories: Array.isArray(d.categories) ? d.categories : [],
        vendors: Array.isArray(d.vendors) ? d.vendors : [],
        part_types: Array.isArray(d.part_types) ? d.part_types : [],
      };
    })().catch((err) => {
      _facetsCache = null;
      throw err;
    });
  }
  return _facetsCache;
}

let _categoriesFallbackCache: Promise<CdnCategory[]> | null = null;
async function listCategoriesFallback(): Promise<CdnCategory[]> {
  if (!_categoriesFallbackCache) {
    _categoriesFallbackCache = (async () => {
      const { data, error } = await supabase
        .from("cdn_products")
        .select("basic_category")
        .eq("is_active", true)
        .not("basic_category", "is", null)
        .limit(50000);
      if (error) throw new Error(error.message);
      const seen = new Set<string>();
      for (const r of (data ?? []) as Array<{ basic_category: string | null }>) {
        const v = (r.basic_category ?? "").trim();
        if (v) seen.add(v);
      }
      return Array.from(seen)
        .map((name) => ({ name, count: 0 }))
        .sort((a, b) => a.name.localeCompare(b.name));
    })().catch((err) => { _categoriesFallbackCache = null; throw err; });
  }
  return _categoriesFallbackCache;
}

export async function listCategories(): Promise<CdnCategory[]> {
  try {
    const { categories } = await getCdnFacets();
    if (categories.length > 0) {
      return categories
        .map((name) => ({ name, count: 0 }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    console.warn("[cdnProducts] facets RPC returned empty categories; using fallback");
  } catch (err) {
    console.error("[cdnProducts] facets RPC failed; using direct-query fallback", err);
  }
  return listCategoriesFallback();
}

export async function listManufacturers(opts: { category?: string; search?: string } = {}): Promise<CdnCategory[]> {
  // Fast path: no filters → use cached RPC.
  if (!opts.category && !opts.search) {
    const { vendors } = await getCdnFacets();
    return vendors
      .map((name) => ({ name, count: 0 }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  return facetCached(`mfrs:${opts.category ?? ""}:${opts.search ?? ""}`, async () => {
    const { data, error } = await supabase.rpc("cdn_manufacturers_search", {
      _limit: 1000,
      ...(opts.search ? { _q: opts.search } : {}),
      ...(opts.category ? { _category: opts.category } : {}),
    });
    if (error) throw new Error(error.message);
    return ((data ?? []) as Array<{ vendor_name: string; cnt: number }>)
      .map((r) => ({ name: r.vendor_name, count: Number(r.cnt) }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  });
}

export function searchProducts(q: string, opts: { page?: number; limit?: number } = {}) {
  return listProducts({
    search: q,
    ...(opts.page !== undefined ? { page: opts.page } : {}),
    ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
  });
}

/**
 * Distinct part_type_name values for a given category (or all categories).
 * Used by the category page "Item type" filter dropdown.
 */
export async function listPartTypes(opts: { category?: string; limit?: number } = {}): Promise<CdnCategory[]> {
  return facetCached(`parttypes:${opts.category ?? ""}:${opts.limit ?? 0}`, async () => {
    // Preferred path: DB-side GROUP BY (~50 rows back instead of 5,000 that
    // the browser previously had to download and count — one of the slowest
    // query shapes in the project at ~700ms avg / 20k calls).
    try {
      const { data, error } = await (supabase.rpc as any)("cdn_part_types_search", {
        _category: opts.category ?? null,
        _limit: 50,
      });
      if (error) throw new Error(error.message);
      return ((data ?? []) as Array<{ part_type_name: string; product_count: number }>)
        .map((r) => ({ name: r.part_type_name, count: Number(r.product_count ?? 0) }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    } catch {
      // Fallback: legacy client-side counting if the RPC is unavailable
      let q = supabase
        .from("cdn_products")
        .select("part_type_name")
        .eq("is_active", true)
        .eq("is_published", true)
        .not("part_type_name", "is", null)
        .neq("part_type_name", "")
        .limit(opts.limit ?? 5000);
      if (opts.category) q = q.eq("basic_category", opts.category);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      const counts = new Map<string, number>();
      for (const r of (data ?? []) as Array<{ part_type_name: string | null }>) {
        const v = (r.part_type_name ?? "").trim();
        if (!v) continue;
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      return Array.from(counts.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    }
  });
}

export async function getProduct(id: string | number): Promise<CdnProduct> {
  const key = String(id);
  // Public-safe column list — excludes wholesale cost, jobber price, distributor
  // info and other internal fields that are revoked from `anon`.
  const COLS =
    "product_id, admin_slug, short_slug, product_title, admin_title, meta_title, meta_description, " +
    "description, description_html, marketing_description, " +
    "application_models, " +
    "part_type_name, vendor_name, basic_category, " +
    "customer_retail_price, admin_price, map_retail, admin_sale_price, customer_cost, admin_compare_at_price, " +
    "image_1_url, image_2_url, image_3_url, image_4_url, image_5_url, image_6_url, admin_images, " +
    "inventory_total, admin_stock_quantity, inv_east, inv_great_lakes, inv_midwest, inv_pacific_northwest, " +
    "inv_texas, inv_california, inv_southeast, inv_florida, " +
    "fedexable, upsable, us_ltl, ups_ground_assessorial, free_shipping, additional_shipping_charge, " +
    "is_active, is_published, is_new_badge, is_featured, show_on_home, " +
    "product_number, vendor_code_product_number, upc, manufacturer_part_no, " +
    "admin_short_description, admin_meta_title, admin_meta_description, admin_meta_keywords, admin_slug, admin_tags, " +
    "admin_mpn, admin_gtin, admin_weight_lbs, admin_length_in, admin_width_in, admin_height_in, " +
    "min_cart_qty, max_cart_qty, low_stock_threshold, backorder_mode, display_availability, display_stock_quantity, " +
    "disable_buy_button, disable_wishlist_button, call_for_price, related_product_ids, cross_sell_product_ids, admin_videos, " +
    "core_charge, case_qty, is_non_returnable, prop65_toxicity, aaia_code, is_hazmat, is_chemical, is_kit, kit_components, " +
    "generic_legacy_name, install_pdf_url, doc_links";
  const ADDON_COLS = ", addon_product_id, related_addon_ids, all_attributes_html, all_features_html";
  // Try slug first (new SEO URLs), then fall back to product_id, then product_number.
  // Look up by either the new short_slug or the legacy admin_slug.
  if (/[a-z-]/i.test(key) && !/^\d+$/.test(key)) {
    const { data } = await supabase
      .from("cdn_products")
      .select(COLS + ADDON_COLS)
      .or(`short_slug.eq.${key},admin_slug.eq.${key}`)
      .maybeSingle();
    if (data) return rowToProduct(data);
    // Legacy URL recovery: older catalog URLs used the slug WITHOUT the leading
    // vendor-code token, e.g. `dv8-hood-hdmb07-02` where the current canonical
    // slug is `dv8hdmb07-02-dv8-hood-hdmb07-02`. Those are now indexed in Search
    // Console as 404s, so resolve them by suffix and let the page 301 to the
    // canonical short slug.
    if (key.includes("-")) {
      const escaped = key.replace(/[%_,()]/g, "");
      const { data: legacy } = await supabase
        .from("cdn_products")
        .select(COLS + ADDON_COLS)
        .like("short_slug", `%-${escaped}`)
        .limit(1)
        .maybeSingle();
      if (legacy) return rowToProduct(legacy);
    }
  }
  const byId = await supabase
    .from("cdn_products")
    .select(COLS + ADDON_COLS)
    .eq("product_id", key)
    .maybeSingle();
  if (byId.error) throw new Error(byId.error.message);
  if (byId.data) return rowToProduct(byId.data);
  const byNum = await supabase
    .from("cdn_products")
    .select(COLS + ADDON_COLS)
    .eq("product_number", key)
    .maybeSingle();
  if (byNum.error) throw new Error(byNum.error.message);
  if (!byNum.data) throw new Error("Product not found");
  return rowToProduct(byNum.data);
}

/** Build the SEO URL path for a product. Prefers the short (meta-title based)
 * slug, then the legacy long slug, then ProductID. */
export function productPath(p: Pick<CdnProduct, "ProductID" | "Slug" | "admin_slug" | "short_slug">): string {
  const short = p.short_slug as string | null | undefined;
  if (short && short.length > 0) return `/parts-catalog/${short}`;
  const slug = (p.Slug ?? p.admin_slug) as string | null | undefined;
  if (slug && slug.length > 0) return `/parts-catalog/${slug}`;
  return `/parts-catalog/${encodeURIComponent(String(p.ProductID))}`;
}

export function productImages(p: CdnProduct): string[] {
  return [
    p.Image_1_Url, p.Image_2_Url, p.Image_3_Url,
    p.Image_4_Url, p.Image_5_Url, p.Image_6_Url,
  ].filter((u): u is string => typeof u === "string" && u.length > 0);
}

export function productAvailability(p: CdnProduct): {
  total: number;
  regions: { name: string; qty: number }[];
} {
  const regions = [
    { name: "East", qty: p.Inv_East ?? 0 },
    { name: "Great Lakes", qty: p.Inv_GreatLakes ?? 0 },
    { name: "Midwest", qty: p.Inv_Midwest ?? 0 },
    { name: "Pacific Northwest", qty: p.Inv_PacificNorthwest ?? 0 },
    { name: "Texas", qty: p.Inv_Texas ?? 0 },
    { name: "California", qty: p.Inv_California ?? 0 },
    { name: "Southeast", qty: p.Inv_Southeast ?? 0 },
    { name: "Florida", qty: p.Inv_Florida ?? 0 },
  ].filter((r) => r.qty > 0);
  // The stored total always wins: a total of 0 means out of stock even if a
  // warehouse column still holds a leftover quantity from an older feed.
  const stored = typeof p.InventoryTotal === "number" ? p.InventoryTotal : null;
  if (stored !== null) {
    return { total: stored, regions: stored > 0 ? regions : [] };
  }
  return { total: regions.reduce((s, r) => s + r.qty, 0), regions };
}

export function productShipping(p: CdnProduct): string[] {
  const methods: string[] = [];
  if (p.UPSable === "True" || p.UPSable === "true" || p.UPSable === "1") methods.push("UPS");
  if (p.US_LTL && String(p.US_LTL).toLowerCase() !== "false" && p.US_LTL !== "0") methods.push("Freight (LTL)");
  return methods;
}

/**
 * Fetch published products by their addon_product_id values (used for the
 * "Related Products" section sourced from the addon feed's AllRelatedProducts).
 */
export async function listProductsByAddonIds(
  addonIds: string[],
  limit = 12,
): Promise<CdnProduct[]> {
  const ids = Array.from(new Set((addonIds || []).map((s) => String(s).trim()).filter(Boolean)));
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from("cdn_products")
    .select(
      "product_id, admin_slug, short_slug, product_title, admin_title, meta_title, admin_meta_title, " +
        "part_type_name, vendor_name, " +
        "customer_retail_price, admin_price, map_retail, admin_sale_price, customer_cost, " +
        "image_1_url, image_2_url, image_3_url, image_4_url, image_5_url, image_6_url, admin_images, " +
        "is_active, is_published, addon_product_id",
    )
    .eq("is_active", true)
    .eq("is_published", true)
    .in("addon_product_id", ids.slice(0, limit));
  if (error) throw new Error(error.message);
  return (data ?? []).map(rowToProduct);
}